import { useLanguage } from "../i18n/LanguageContext";
import { useState, useEffect, useRef } from "react";
import { FiGithub, FiLinkedin } from "react-icons/fi";

export default function Contact() {
  const { t } = useLanguage();
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [submitDetails, setSubmitDetails] = useState("");
  const sending = useRef(false);
  const activeRequest = useRef(null);

  useEffect(
    () => () => {
      activeRequest.current?.abort();
      activeRequest.current = null;
    },
    [],
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (sending.current) return;
    const form = event.currentTarget;
    const controller = new AbortController();
    activeRequest.current = controller;
    sending.current = true;
    setSubmitLoading(true);
    setSubmitStatus("sending");
    setSubmitDetails("");
    let timedOut = false;
    const timeout = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, 20000);

    try {
      const response = await fetch(form.action, {
        method: "POST",
        body: new FormData(form),
        headers: { Accept: "application/json" },
        signal: controller.signal,
      });
      if (!response.ok) {
        const result = await response.json().catch(() => null);
        const details = Array.isArray(result?.errors)
          ? result.errors
              .map((error) => error.message)
              .filter((message) => typeof message === "string")
              .join(" ")
          : typeof result?.error === "string"
            ? result.error
            : "";
        if (controller.signal.aborted) throw new Error("Request aborted");
        setSubmitDetails(details);
        setSubmitStatus("error");
        return;
      }
      if (controller.signal.aborted) throw new Error("Request aborted");
      form.reset();
      setSubmitStatus("success");
    } catch (_) {
      if (activeRequest.current === controller) {
        setSubmitStatus(timedOut ? "timeout" : "error");
      }
    } finally {
      clearTimeout(timeout);
      sending.current = false;
      if (activeRequest.current === controller) {
        activeRequest.current = null;
        setSubmitLoading(false);
      }
    }
  };

  const handleInvalid = (event) => {
    setSubmitStatus(
      event.target.validity.patternMismatch
        ? "invalidPhone"
        : event.target.validity.typeMismatch
          ? "invalidEmail"
          : "required",
    );
    setSubmitDetails("");
  };

  useEffect(() => {
    // Set page keywords
    document.querySelector('meta[name="keywords"]').setAttribute(
      "content",
      `full-stack developer, software developer, software engineer, computer engineer, software developer in Kuwait,Kuwait, 
          Coded Academy, AUM, Ruby on Rails, Ruby, React, JavaScript, TypeScript, Nextjs, HTML, CSS, website, application, 
          Contact, hello, github, stack over flow, linkedIn, buy me a coffee, contact form`,
    );
  }, []);

  return (
    <div id="contact" className="contact">
      <div className="contact-postcard">
        <div className="contact-introduction">
          <h1>{t("contact.heading")}</h1>
          <p>{t("contact.subtitle")}</p>
          <div className="contact-socials">
            <a
              href="https://www.linkedin.com/in/hessah-al-hamadi-715795170/"
              target="_blank"
              rel="noreferrer"
            >
              <FiLinkedin aria-hidden="true" /> <span>LinkedIn</span>
              <span aria-hidden="true">↗</span>
            </a>
            <a
              href="https://github.com/Hessah95"
              target="_blank"
              rel="noreferrer"
            >
              <FiGithub aria-hidden="true" /> <span>GitHub</span>
              <span aria-hidden="true">↗</span>
            </a>
          </div>
        </div>
        <div className="contact-form">
          <form
            action="https://formspree.io/f/xqkrjwzk"
            method="post"
            onSubmit={handleSubmit}
            onInvalid={handleInvalid}
            aria-busy={submitLoading}
          >
            <div className="contact-field">
              <label htmlFor="contact-name">{t("contact.name")}</label>
              <input
                type="text"
                name="name"
                id="contact-name"
                autoComplete="name"
                aria-describedby={submitStatus ? "contact-feedback" : undefined}
                disabled={submitLoading}
                required
              />
            </div>
            <div className="contact-field">
              <label htmlFor="contact-email">{t("contact.email")}</label>
              <input
                type="email"
                name="email"
                id="contact-email"
                autoComplete="email"
                dir="ltr"
                aria-describedby={submitStatus ? "contact-feedback" : undefined}
                disabled={submitLoading}
                required
              />
            </div>
            <div className="contact-field">
              <label htmlFor="contact-phone">{t("contact.phone")}</label>
              <input
                type="tel"
                name="phone"
                id="contact-phone"
                autoComplete="tel"
                dir="ltr"
                placeholder="+965 1234 5678"
                pattern="[+]?[0-9][0-9 ]{5,24}"
                aria-describedby={
                  submitStatus
                    ? "contact-phone-hint contact-feedback"
                    : "contact-phone-hint"
                }
                disabled={submitLoading}
                required
              />
              <small id="contact-phone-hint">{t("contact.phoneHint")}</small>
            </div>
            <div className="contact-field">
              <label htmlFor="contact-message">
                {t("contact.messageLabel")}
              </label>
              <textarea
                id="contact-message"
                name="message"
                rows={5}
                placeholder={t("contact.message")}
                aria-describedby={submitStatus ? "contact-feedback" : undefined}
                disabled={submitLoading}
                required
              />
            </div>
            <button
              type="submit"
              className="contact-send"
              disabled={submitLoading}
            >
              {submitLoading ? t("contact.sending") : t("contact.send")}
              <span aria-hidden="true">↗</span>
            </button>
            {submitStatus && (
              <p
                className="contact-feedback"
                id="contact-feedback"
                role={
                  ["success", "sending"].includes(submitStatus)
                    ? "status"
                    : "alert"
                }
              >
                {t(`contact.${submitStatus}`)}
                {submitDetails && (
                  <span className="d-block">{submitDetails}</span>
                )}
              </p>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
